import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { UtilitiesGlobalSearchRecordResponse, UtilitiesPaginationInfo } from '@libs/generated-types/utilities';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AvatarModule } from 'primeng/avatar';
import { BadgeModule } from "primeng/badge";
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { ChipModule } from "primeng/chip";
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { MenuModule } from 'primeng/menu';
import { OverlayBadgeModule } from "primeng/overlaybadge";
import { Popover, PopoverModule } from 'primeng/popover';
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { StyleClassModule } from 'primeng/styleclass';
import { GlobalSearchService } from '../services/global-search.service';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';

@Component({
    selector: 'app-search',
    standalone: true,
    imports: [
        RouterModule,
        CommonModule,
        FormsModule,
        StyleClassModule,
        PopoverModule,
        TranslateModule,
        BreadcrumbModule,
        IconFieldModule,
        InputIconModule,
        InputTextModule,
        ButtonModule,
        MenuModule,
        AvatarModule,
        ProgressSpinnerModule,
        OverlayBadgeModule,
        BadgeModule,
        ChipModule,
        ToastModule
    ],
    template: ` 
    <div style="width: 100%;" #searchBar>
        <p-iconfield iconPosition="left" class="search-bar">
            <p-inputicon styleClass="pi pi-search" />
            <input 
                pInputText 
                type="text" 
                class="search-bar" 
                placeholder="{{ 'topbar.search.label' | translate }}" 
                [(ngModel)]="searchQuery"
                (input)="onSearchInput($event)"
            />
        </p-iconfield>
    </div>
    <p-popover #searchPopover [appendTo]="searchBar" styleClass="search-popover">
      <ng-template #content>
        <div *ngIf="loadingSearch" class="p-4 flex items-center justify-center">
          <p-progressSpinner styleClass="w-8 h-8" strokeWidth="4" fill="transparent" animationDuration=".5s">
          </p-progressSpinner>
        </div>
        <div *ngIf="!loadingSearch && searchResults && searchResults.length > 0" class="max-h-[400px] overflow-y-auto">
          <div>
            <ul class="list-none p-0 m-0 flex flex-col">
              <ng-container *ngFor="let result of searchResults">
                <li (click)="onSearchResultClick(result)"
                class="flex items-center flex-row justify-between gap-2 px-4 py-3 hover:bg-emphasis cursor-pointer border border-solid border-gray-100">
                    <div class="flex items-center flex-row">
                        <img [src]="result?.cover?.url" style="width: 80px; margin-right: 20px" />
                        <div>
                            <span class="font-medium">{{ result.title }}</span>
                            <p class="text-sm text-gray-600">{{ result.description }}</p>
                            <p-chip severity="info">{{ result.type }}</p-chip>
                        </div>
                    </div>
                    <p-button icon="pi pi-angle-right" [rounded]="true" [text]="true" ></p-button>
                </li>
              </ng-container>
            </ul>
              <div class="text-center my-3">
                <a (click)="loadMore($event)" class="underline cursor-pointer" *ngIf="searchPagination.hasNext && !loadingMore">{{ 'topbar.search.show-more' | translate }}</a>
              </div>
            </div>
        </div>
        <div *ngIf="!loadingSearch && searchQuery.length >= 3 && (!searchResults || searchResults.length === 0)" class="p-4">
          <span>{{ 'topbar.search.no-results' | translate }}</span>
        </div>
      </ng-template>
    </p-popover>
    <p-toast position="top-right" />
`,
    styles: [
        `
      ::ng-deep .p-popover-content {
        padding: 0 !important;
      }
      
      ::ng-deep .p-popover.p-component.search-popover {
        min-width: 700px !important;
      }
    `
    ]
})
export class AppSearch implements OnInit {
    searchQuery: string = '';
    loadingSearch: boolean = false;
    searchResults: UtilitiesGlobalSearchRecordResponse[] = [];
    searchPagination: Partial<UtilitiesPaginationInfo> = {
        page: 0,
        size: 5,
        hasNext: true
    };
    private searchTimeout: any;
    loadingMore: boolean = false;
    @ViewChild('searchPopover') searchPopover!: Popover;

    constructor(
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private translate: TranslateService,
        private searchService: GlobalSearchService,
        private readonly messageService: MessageService
    ) {

    }

    ngOnInit(): void {
    }

    onSearchInput(event?: any) {
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }

        if (this.searchQuery.length < 3) {
            this.searchPopover.hide();
            return;
        }

        if (!this.searchPopover.overlayVisible)
            this.searchPopover.toggle(event);

        this.loadingSearch = true;
        this.searchTimeout = setTimeout(() => {
            this.performSearch();
        }, 300);
    }

    performSearch() {
        this.searchResults = [];
        this.searchPagination = { page: 0, size: 5, hasNext: false };
        this.fetchPage(1, false);
    }

    loadMore(event: Event) {
        event.stopPropagation();
        if (this.loadingMore || !this.searchPagination.hasNext) return;
        this.fetchPage((this.searchPagination.page ?? 0) + 1, true);
    }

    private fetchPage(page: number, append: boolean) {
        const searchQuery = this.searchQuery;
        append ? (this.loadingMore = true) : (this.loadingSearch = true);

        this.searchService.getResults({
            search: searchQuery,
            page,
            size: this.searchPagination.size ?? 5
        }).subscribe({
            next: (data) => {
                if (searchQuery !== this.searchQuery) return;
                const items = data.items || [];
                this.searchResults = append ? [...this.searchResults, ...items] : items;
                this.searchPagination = data.paginationInfo;
            },
            error: (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('global.error.label'),
                    detail: this.translate.instant('global.error.generic.detail'),
                });
                if (!append) this.searchResults = [];
            },
            complete: () => {
                this.loadingSearch = false;
                this.loadingMore = false;
            }
        });
    }

    onSearchResultClick(result: UtilitiesGlobalSearchRecordResponse) {
        this.searchPopover.hide();
        this.searchQuery = '';
        this.searchResults = [];
        console.log('Navigating to:', '/' + result.webAppUri);
        this.router.navigateByUrl('/' + result.webAppUri);
    }
}
